import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';

/**
 * Values understood by `snfs/saveAdvancedDirectiveUserAction`. The backend
 * stores a single value per (patientId, facilityId), so the three stateful
 * icons in this cell are mutually exclusive on the backend even though they
 * render as independent toggles — clicking one clears whichever was on.
 */
export type UserActionValue =
  | 'reviewed_approved_mismatch'
  | 'reviewed_disagreed_ai'
  | 'retry_document_fetch'
  | null;

@Component({
  selector: 'report-table-v4-actions-cell',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="rtad-actions">
      <!-- 1. View — always rendered; greyed out when no source document -->
      <button type="button"
              class="rtad-btn rtad-btn--view"
              [class.is-disabled]="!hasFile"
              [disabled]="!hasFile"
              (click)="onView($event)"
              [title]="hasFile ? 'View source document' : 'No document available'"
              [attr.aria-label]="hasFile ? 'View source document' : 'No document available'">
        <svg class="rtad-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      </button>

      <!-- 2. Download — always rendered; greyed out when no source document -->
      <button type="button"
              class="rtad-btn rtad-btn--download"
              [class.is-disabled]="!hasFile"
              [disabled]="!hasFile"
              (click)="onDownload($event)"
              [title]="hasFile ? 'Download document' : 'No document available'"
              [attr.aria-label]="hasFile ? 'Download document' : 'No document available'">
        <svg class="rtad-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      </button>

      <!-- Toggles 3-5 are not offered when the parent passes no save handler (view-only roles). -->
      <ng-container *ngIf="canSaveAction">
      <!-- 3. Approve mismatch (toggle) — greyed out when there is no order to review -->
      <button type="button"
              class="rtad-btn rtad-btn--approve"
              [class.is-on]="hasOrder && selectedAction === 'reviewed_approved_mismatch'"
              [class.is-disabled]="!hasOrder"
              [disabled]="!hasOrder"
              (click)="toggleAction('reviewed_approved_mismatch', $event)"
              [title]="!hasOrder ? 'No order to review' : (selectedAction === 'reviewed_approved_mismatch' ? 'Mismatch marked as acknowledged' : 'Acknowledge review of mismatch')"
              [attr.aria-label]="!hasOrder ? 'No order to review' : (selectedAction === 'reviewed_approved_mismatch' ? 'Mismatch marked as acknowledged' : 'Acknowledge review of mismatch')">
        <svg class="rtad-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </button>

      <!-- 4. Flag issue (toggle) — greyed out when there is no order to review -->
      <button type="button"
              class="rtad-btn rtad-btn--flag"
              [class.is-on]="hasOrder && selectedAction === 'reviewed_disagreed_ai'"
              [class.is-disabled]="!hasOrder"
              [disabled]="!hasOrder"
              (click)="toggleAction('reviewed_disagreed_ai', $event)"
              [title]="!hasOrder ? 'No order to review' : (selectedAction === 'reviewed_disagreed_ai' ? 'An incorrect AI extraction was reported' : 'Report incorrect AI extraction')"
              [attr.aria-label]="!hasOrder ? 'No order to review' : (selectedAction === 'reviewed_disagreed_ai' ? 'An incorrect AI extraction was reported' : 'Report incorrect AI extraction')">
        <svg class="rtad-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="13"/>
          <line x1="12" y1="16.5" x2="12.01" y2="16.5"/>
        </svg>
      </button>

      <!-- 5. Run again (toggle) — available whenever a PCC order exists, even if no document was found -->
      <button type="button"
              class="rtad-btn rtad-btn--retry"
              [class.is-on]="canRetry && selectedAction === 'retry_document_fetch'"
              [class.is-disabled]="!canRetry"
              [disabled]="!canRetry"
              (click)="toggleAction('retry_document_fetch', $event)"
              [title]="!canRetry ? 'No order to review' : (selectedAction === 'retry_document_fetch' ? 'Patient marked to re-run in upcoming AI run cycle' : 'Mark patient to re-run in upcoming AI run cycle')"
              [attr.aria-label]="!canRetry ? 'No order to review' : (selectedAction === 'retry_document_fetch' ? 'Patient marked to re-run in upcoming AI run cycle' : 'Mark patient to re-run in upcoming AI run cycle')">
        <svg class="rtad-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="23 4 23 10 17 10"/>
          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
        </svg>
      </button>
      </ng-container>
    </div>
  `,
  styles: [`
    .rtad-actions {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 2px 0;
    }

    .rtad-btn {
      padding: 5px;
      width: 28px;
      height: 28px;
      border: 1px solid transparent;
      border-radius: 4px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
      background: #f7f9fc;
      color: #6b7280;
      border-color: #e0e0e0;
    }

    .rtad-btn:hover {
      background: #eef2f7;
      border-color: #c8d1dd;
    }

    .rtad-icon {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }

    /* View — Blue */
    .rtad-btn--view {
      background: #e6f0ff;
      color: #0066cc;
      border-color: #cce0ff;
    }
    .rtad-btn--view:hover {
      background: #cce0ff;
      border-color: #99c2ff;
      box-shadow: 0 1px 4px rgba(0, 102, 204, 0.15);
    }

    /* Download — Teal */
    .rtad-btn--download {
      background: #e6fffa;
      color: #009980;
      border-color: #b3f0e6;
    }
    .rtad-btn--download:hover {
      background: #ccfff5;
      border-color: #80e6d6;
      box-shadow: 0 1px 4px rgba(0, 204, 153, 0.15);
    }

    /* Approve — neutral off, green on */
    .rtad-btn--approve.is-on {
      background: #c8e6c9;
      color: #1b5e20;
      border-color: #a5d6a7;
    }
    .rtad-btn--approve.is-on:hover {
      background: #a5d6a7;
      border-color: #81c784;
    }

    /* Flag — neutral off, amber/orange on */
    .rtad-btn--flag.is-on {
      background: #ffe082;
      color: #e65100;
      border-color: #ffd54f;
    }
    .rtad-btn--flag.is-on:hover {
      background: #ffd54f;
      border-color: #ffca28;
    }

    /* Run again — neutral off, blue on */
    .rtad-btn--retry.is-on {
      background: #dbeafe;
      color: #1d4ed8;
      border-color: #bfdbfe;
    }
    .rtad-btn--retry.is-on:hover {
      background: #bfdbfe;
      border-color: #93c5fd;
    }

    /* Disabled state — View/Download when no source document exists */
    .rtad-btn:disabled,
    .rtad-btn.is-disabled {
      background: #f5f5f5 !important;
      color: #b0b0b0 !important;
      border-color: #e4e4e4 !important;
      cursor: not-allowed !important;
      opacity: 0.55;
      box-shadow: none !important;
    }
    .rtad-btn:disabled:hover,
    .rtad-btn.is-disabled:hover {
      background: #f5f5f5 !important;
      border-color: #e4e4e4 !important;
    }
  `],
})
export class ReportTableV4ActionsCellComponent implements ICellRendererAngularComp {
  private params: any;
  fileKey: string | null = null;
  hasOrder = true;
  /** True when a PCC order exists — enables the retry button even when no document was found. */
  canRetry = true;
  selectedAction: UserActionValue = null;
  canSaveAction = false;

  /** True when a source document is actually available for this row. */
  get hasFile(): boolean {
    return !!this.fileKey && this.fileKey !== '-';
  }

  constructor(private cdr: ChangeDetectorRef) {}

  agInit(params: any): void {
    this.ingest(params);
  }

  refresh(params: any): boolean {
    this.ingest(params);
    return true;
  }

  private ingest(params: any): void {
    this.params = params;
    this.canSaveAction = !!params?.context?.componentParent?.onOpenOptionsMenu;

    this.fileKey =
      params?.data?.actions?.fileName ??
      params?.data?.fileName ??
      (typeof params?.data?.actions === 'string' ? params.data.actions : null);

    // Rows where the status is "No Order" come from BedBoard and have
    // `actions: null`; for those we grey the toggle icons (no order to
    // review) but still show view/download if a file happens to exist.
    const status = params?.data?.status?.value ?? params?.data?.status ?? '';
    const matchValue = params?.data?.match?.value ?? params?.data?.match ?? '';
    const hasPccOrder =
      status?.toLowerCase() !== 'no order'
      && params?.data?.actions != null;
    this.hasOrder = hasPccOrder
      && String(matchValue).toLowerCase() !== 'no data';
    // Retry (mark for next AI cycle) is available whenever a PCC order
    // exists, even if no document was found yet — the user may add the
    // document and want to trigger fetch on the next hourly run.
    this.canRetry = hasPccOrder;

    const raw =
      params?.data?.userActionValue ??
      params?.data?.userAction?.selectedId ??
      null;
    this.selectedAction = this.normalizeAction(raw);

    // AG Grid's cell lifecycle (agInit / refresh) can fire outside an
    // Angular change-detection cycle, so the `[class.is-on]` bindings
    // won't re-evaluate unless we explicitly re-run detection on this
    // view. Without this, the saved `userActionValue` returned by the
    // server on reload reaches `selectedAction` but never makes it into
    // the DOM — which is why the icons appeared off after reload even
    // though the DB had the correct value.
    this.cdr.detectChanges();
  }

  private normalizeAction(value: unknown): UserActionValue {
    switch (value) {
      case 'reviewed_approved_mismatch':
      case 'reviewed_disagreed_ai':
      case 'retry_document_fetch':
        return value;
      default:
        return null;
    }
  }

  onView(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.hasFile) return;
    this.params?.context?.componentParent?.onViewFile?.(this.fileKey);
  }

  onDownload(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.hasFile) return;
    this.params?.context?.componentParent?.onDownloadFile?.(this.fileKey);
  }

  /**
   * Toggle one of the three mutually-exclusive `userAction` values. Clicking
   * the currently-selected icon clears the action; clicking a different icon
   * replaces it. Persists through the parent's `onOpenOptionsMenu` callback.
   *
   * We also write the new value back onto `params.data.userActionValue`
   * optimistically — AG Grid's cell lifecycle (range-selection refresh,
   * re-rendering) calls `refresh()` which re-reads the row, and without the
   * mutation the visual "is-on" state would get clobbered by the stale
   * server-provided value on the next refresh cycle.
   */
  toggleAction(action: Exclude<UserActionValue, null>, e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const allowed = action === 'retry_document_fetch' ? this.canRetry : this.hasOrder;
    if (!allowed) return;
    const next: UserActionValue = this.selectedAction === action ? null : action;
    this.selectedAction = next;
    if (this.params?.data) {
      this.params.data.userActionValue = next;
      if (this.params.data.userAction) {
        this.params.data.userAction.selectedId = next;
      }
    }
    this.cdr.detectChanges();
    this.params?.context?.componentParent?.onOpenOptionsMenu?.({
      row: this.params?.data,
      action: next,
    });
  }
}
